import * as Cl from '@design-system/component-library';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useDebounce } from '../../common/hooks/useDebounce.js';
import { filterMsg, searchPlaceHolderMsg, t } from '../../common/i18n/index.js';
import { CloseButton, InputSearch } from '../InputSearch/InputSearch.js';
import { ToggleButton } from '../ToggleButton/ToggleButton.js';
import { TableUrlParams } from './tableConstants.js';
import { resetPagingParams } from './tableUtils.js';

import './ListSearch.scss';

const MIN_SEARCH_QUERY_LENGTH = 2;

interface ListSearchProps {
  className?: string;
  defaultItemsPerPageOption?: number;
  filtersOpen?: boolean;
  hasCursorNavigation?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  onSearch?: () => void;
  tooltipText?: string;
}

export interface DefaultListSearchParams {
  search?: string;
  sort?: string;
  offset?: string;
  order?: string;
  limit?: string;
}

export const ListSearch = ({
  className,
  filtersOpen,
  hasCursorNavigation = false,
  setFiltersOpen,
  onSearch,
  tooltipText,
}: ListSearchProps) => {
  const navigate = useNavigate();
  const { hash, search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const searchQueryParam = queryParams.get(TableUrlParams.SEARCH);
  const [currentSearch, setCurrentSearch] = useState(searchQueryParam || '');

  const doSearchDebounced = useDebounce(() => {
    navigate(`${pathname}?${queryParams}${hash}`, { replace: true });
  }, 1000);

  const handleSearch = (query: string) => {
    setCurrentSearch(query);
    if (query && query.length >= MIN_SEARCH_QUERY_LENGTH) {
      queryParams.set(TableUrlParams.SEARCH, query);
      resetPagingParams(queryParams, hasCursorNavigation);
      if (onSearch) {
        onSearch();
      }
    } else {
      queryParams.delete(TableUrlParams.SEARCH);
    }
    doSearchDebounced();
  };

  const toggleFilters = () => {
    if (setFiltersOpen) {
      setFiltersOpen(!filtersOpen);
    }
  };

  return (
    <div className={`of-list-search ${setFiltersOpen ? dsClass.MARGIN_BOTTOM_2 : ''} ${className || ''}`}>
      <div className={dsClass.INPUTGROUP_INPUTCONTAINER}>
        <div className={`${dsClass.INPUT} ${dsClass.INPUT_WITHICON} of-list-search__input`}>
          <InputSearch
            name="tableSearch"
            type="search"
            placeholder={t.GGLB(searchPlaceHolderMsg)}
            onChange={e => handleSearch(e.target.value)}
            value={currentSearch}
          >
            {!!currentSearch && <CloseButton onClick={() => handleSearch('')} />}
          </InputSearch>
        </div>
        {tooltipText && (
          <div
            className={`${dsClass.MARGIN_LEFT_2} ${dsClass.MARGIN_RIGHT_2} ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
          >
            <Cl.Tooltip
              triggerElement={<Cl.Icon icon="information" color="blue-600" />}
              placement="top"
              i18n_tooltip_contentText={tooltipText}
            />
          </div>
        )}
        {setFiltersOpen && (
          <div className="of-toggle-filters-button-container">
            <div className="of-toggle-filters-button">
              <ToggleButton buttonLabel={t.XUFV(filterMsg)} isOpen={!!filtersOpen} onClick={toggleFilters} />
            </div>
            <div className="of-toggle-filters-button-mobile">
              <Cl.Button size="l" onClick={toggleFilters} aria-label={t.XUFV(filterMsg)}>
                <Cl.Icon color="blue-600" icon="filter" />
              </Cl.Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
